import { Component } from '@theme/component';

const STORAGE_KEY = 'dmd-price-mode';
const CHANGE_EVENT = 'price-mode:change';

/**
 * @returns {'gross' | 'net'}
 */
function currentMode() {
  return document.documentElement.dataset.priceMode === 'net' ? 'net' : 'gross';
}

/**
 * Brutto/Netto-Schalter. Reine Anzeige: setzt `data-price-mode` auf <html>, CSS blendet
 * die server-gerenderten Brutto- und Netto-Werte um (assets/base.css). Gemerkt wird der
 * Modus nur für diesen Tab (sessionStorage); das Früh-Script in layout/theme.liquid setzt
 * ihn vor dem ersten Rendern.
 *
 * @extends {Component}
 */
class PriceModeToggle extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.#sync);

    // Horizon morpht Warenkorb und Produktbereich aus Server-HTML; das setzt aria-pressed
    // auf den Server-Standard (brutto) zurück. #sync schreibt nur bei Abweichung — keine Schleife.
    this.#observer = new MutationObserver(this.#sync);
    this.#observer.observe(this, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Schaltet die ganze Seite auf brutto oder netto (per `on:click="/select/<mode>"`).
   * @param {string} mode - 'gross' oder 'net'
   */
  select(mode) {
    if (mode !== 'gross' && mode !== 'net') return;

    const root = document.documentElement;
    if (mode === 'net') {
      root.dataset.priceMode = 'net';
    } else {
      delete root.dataset.priceMode;
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      // Gesperrter Speicher (z. B. privater Modus): umschalten klappt trotzdem, nur ohne Merken.
    }

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { mode } }));
  }

  #sync = () => {
    const mode = currentMode();
    for (const button of this.querySelectorAll('button[data-mode]')) {
      const pressed = String(button.getAttribute('data-mode') === mode);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    }
  };
}

if (!customElements.get('price-mode-toggle')) {
  customElements.define('price-mode-toggle', PriceModeToggle);
}
